'use client'

import dynamic from 'next/dynamic'
import type { FocusEvent, MouseEvent } from 'react'
import { Grid, Typography, CircularProgress } from '@mui/material'
import { useFieldArray, useWatch } from 'react-hook-form'

// Types
import type { FormProps } from '@/types/components/organisms/forms'

// Configurations
import APIConfig from '@/config/api'
import LocaleConfig from '@/config/locale'

// Utils
import getLocaleDirection from '@/utils/locale/direction'
import ToEnglishNumber from '@/utils/number/to-english-number'
import ToPersianNumber from '@/utils/number/to-persian-number'

// Components
const TextFieldMolecule = dynamic(() => import('@/components/molecules/field/text'))
const PrimaryButtonAtom = dynamic(() => import('@/components/atoms/button/primary'))
const SecondaryButtonAtom = dynamic(() => import('@/components/atoms/button/secondary'))
const IconButtonAtom = dynamic(() => import('@/components/atoms/button/icon'))
const BackIconAtom = dynamic(() => import('@/components/atoms/icon/two-tone/back'))
const ForwardIconAtom = dynamic(() => import('@/components/atoms/icon/two-tone/forward'))

const VerificationCodeOrganism = (props: FormProps) => {
	// Props
	const { lng, locale, control, formState, loading, values, setValue, setFocus, getValues, reset, onConfirm, onReset, onCancel } = props

	// Variables
	const lngDir = getLocaleDirection(lng ?? LocaleConfig.default)
	const codeValues = useWatch({ control, name: 'code' })
	const { username, timer } = values ?? {}
	const errors = formState?.errors ?? {}
	const verificationCodeValue = codeValues.map((item: { value: string }) => item.value.trim()).join('')

	// Form
	const { fields } = useFieldArray({
		control: control,
		name: 'code'
	})

	const focusNextInput = (index: number) => {
		if (!setFocus || index >= APIConfig.verificationNumber - 1) return

		setFocus(`code.${index + 1}.value`)
	}

	const getCellValue = (cellIndex: number) => {
		const rawValue = getValues ? getValues(`code.${cellIndex}.value`) : codeValues[cellIndex]?.value

		return ToEnglishNumber(String(rawValue ?? '')).replace(/\D/g, '')
	}

	const parseDigitKey = (key: string) => ToEnglishNumber(key).replace(/\D/g, '').slice(-1)

	const getInputElement = (event: FocusEvent | MouseEvent) => {
		const target = event.target

		if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) return target

		const root = event.currentTarget

		if (!(root instanceof HTMLElement)) return null

		const input = root.querySelector('input, textarea')

		if (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) return input

		return null
	}

	const selectFilledInput = (cellIndex: number) => (event: FocusEvent | MouseEvent) => {
		if (!getCellValue(cellIndex)) return

		const input = getInputElement(event)

		if (!input) return

		requestAnimationFrame(() => input.select())
	}

	const handleDeleteBackward = (index: number) => {
		if (!setValue || !setFocus) return

		const currentValue = getCellValue(index)

		if (currentValue) {
			setValue(`code.${index}.value`, '')
			return
		}

		for (let cellIndex = index - 1; cellIndex >= 0; cellIndex--) {
			if (getCellValue(cellIndex)) {
				setValue(`code.${cellIndex}.value`, '')
				setFocus(`code.${cellIndex}.value`)
				return
			}
		}

		if (index > 0) setFocus(`code.${index - 1}.value`)
	}

	return (
		<Grid
			component="form"
			container
			spacing={2}
			sx={{
				flexDirection: 'column',
				justifyContent: 'center',
				alignItems: 'center',
				width: 1,
				minHeight: 1,
				borderRadius: 3
			}}
		>
			<Grid
				size={12}
				sx={{
					display: 'flex',
					justifyContent: 'end'
				}}
			>
				<IconButtonAtom
					icon={lngDir === 'rtl' ? <ForwardIconAtom /> : <BackIconAtom />}
					onClick={() => {
						if (reset) reset({ code: Array.from(Array(APIConfig.verificationNumber).keys()).map(() => ({ value: '' })) })
						if (onCancel) onCancel()
					}}
				/>
			</Grid>

			<Grid size={12}>
				<Typography
					component="h2"
					sx={{
						fontSize: 24,
						fontWeight: 700,
						lineHeight: 1.8,
						letterSpacing: -0.02,
						textAlign: 'center',
						color: '#1c1c1c'
					}}
				>
					{locale.form.label.enter_confirmation_code}
				</Typography>

				{username && (
					<Typography
						component="p"
						dangerouslySetInnerHTML={{ __html: locale.form.helper.confirmationCode.replace('{{mobile}}', lng === 'fa' ? ToPersianNumber(username) : username) }}
						sx={{
							fontSize: 16,
							lineHeight: 1.5,
							letterSpacing: -0.02,
							textAlign: 'justify',
							textAlignLast: 'center',
							color: '#4a4a4a'
						}}
					/>
				)}
			</Grid>

			<Grid size={12}>
				<Grid
					container
					spacing={2}
					sx={{
						justifyContent: 'space-between',
						direction: lngDir
					}}
				>
					{fields.map((field, index) => (
						<Grid key={field.id} size={12 / APIConfig.verificationNumber} sx={{ maxWidth: 60 }}>
							<TextFieldMolecule
								lng={lng}
								control={control}
								type="tel"
								name={`code.${index}.value`}
								pattern="digit"
								max={1}
								onFocus={selectFilledInput(index)}
								onClick={selectFilledInput(index)}
								onKeyDown={event => {
									const typedDigit = parseDigitKey(event.key)

									if (typedDigit && getCellValue(index)) {
										event.preventDefault()

										if (setValue) setValue(`code.${index}.value`, typedDigit)
										focusNextInput(index)
										return
									}

									if (event.key === 'Backspace' || event.key === 'Delete') {
										event.preventDefault()
										handleDeleteBackward(index)
										return
									}

									if ((event.key === 'v' && event.ctrlKey) || (event.key === 'v' && event.metaKey)) {
										event.preventDefault()

										navigator.clipboard.readText().then(text => {
											const pastedNumbers = ToEnglishNumber(text).match(/\d/g)

											if (!pastedNumbers || !setValue) return

											const values = pastedNumbers.slice(0, APIConfig.verificationNumber)

											values.forEach((value, i) => {
												setValue(`code.${i}.value`, value)
											})

											if (values.length < APIConfig.verificationNumber) focusNextInput(values.length - 1)
											else if (setFocus) setFocus(`code.${APIConfig.verificationNumber - 1}.value`)
										})
									}
								}}
								onChange={value => {
									const digit = ToEnglishNumber(String(value ?? '')).replace(/\D/g, '')

									if (digit) focusNextInput(index)
								}}
							/>
						</Grid>
					))}
				</Grid>
			</Grid>

			<Grid size={12} sx={{ '& .MuiButtonBase-root': { width: 1 } }}>
				<Grid container spacing={1}>
					<Grid size={7}>
						<SecondaryButtonAtom
							text={locale.form.button.confirm}
							type="submit"
							size="large"
							loading={loading}
							disabled={loading || verificationCodeValue.length !== APIConfig.verificationNumber || Object.keys(errors).length > 0}
							onClick={onConfirm}
						/>
					</Grid>

					{!timer ? (
						<Grid size={5} sx={{ '& .MuiButtonBase-root': { bgcolor: 'primary.dark' } }}>
							<PrimaryButtonAtom text={locale.form.button.recieveCodeAgain} size="large" disabled={timer > 0} onClick={onReset} />
						</Grid>
					) : (
						<Grid
							size={5}
							sx={{
								display: 'inline-flex',
								justifyContent: 'center',
								alignItems: 'center',
								position: 'relative',
								bgcolor: 'action.disabledBackground',
								borderRadius: 1
							}}
						>
							<CircularProgress variant="determinate" color="secondary" size={32} value={(timer / APIConfig.verificationTimer) * 100} />

							<Typography
								component="p"
								sx={{
									position: 'absolute',
									top: '50%',
									left: '50%',
									fontSize: 14,
									fontWeight: 700,
									lineHeight: 1.5,
									letterSpacing: -0.02,
									textAlign: 'center',
									color: 'secondary.main',
									transform: 'translate(-50%, -50%)'
								}}
							>
								{lng === 'fa' ? ToPersianNumber(timer) : timer}
							</Typography>
						</Grid>
					)}
				</Grid>
			</Grid>
		</Grid>
	)
}

export default VerificationCodeOrganism
